//
// # Box Select Mode
//
// Drag to draw a selection rectangle.
// On release, all entities whose bounding boxes intersect the rectangle are selected.
// Shift-held on release adds to the existing selection instead of replacing it.
//
// Two entry points:
// - `BoxSelect.enter()`: mousedown already happened, anchor at current mouse position.
//   Drag to resize, mouseup to accept.
// - `BoxSelectWait.enter()`: keyboard entry (B key), starts immediately at current
//   mouse position. Move to resize, left-click to accept, right-click/Esc to cancel.
//

// Local Imports
import type { InteractionHost } from "../interaction_host";
import { BoxDraw, BoxDrawWait } from "./box_draw";

// Keyboard entry (B key): start box-select immediately at current mouse position.
// Left-click to accept, right-click or Esc to cancel.
export class BoxSelectWait extends BoxDrawWait {
  protected readonly inner: BoxSelect;

  constructor(editor: InteractionHost, additive: boolean) {
    super();
    // Go straight into a live selection rectangle anchored at the cursor.
    this.inner = new BoxSelect(
      editor,
      { ...editor.uiState.mousePos.sch },
      additive,
      "Box Select: left-click to accept, right-click to cancel"
    );
  }

  static enter(
    editor: InteractionHost,
    additive: boolean = false
  ): BoxSelectWait {
    const mode = new BoxSelectWait(editor, additive);
    editor.setMode(mode);
    return mode;
  }
}

// Drag-based box select (entered from mousedown on blank space).
export class BoxSelect extends BoxDraw {
  private readonly additive: boolean;

  constructor(
    editor: InteractionHost,
    anchor: { x: number; y: number },
    additive: boolean,
    status: string = ""
  ) {
    super(editor, anchor, "box-select", status);
    this.additive = additive;
  }

  // Enter box-select mode from a mouse-down event.
  // `additive` controls whether selection is added to (Shift held) or replaced.
  static enter(editor: InteractionHost, additive: boolean): BoxSelect {
    const anchor = { ...editor.uiState.mousePos.sch };
    const mode = new BoxSelect(editor, anchor, additive);
    editor.setMode(mode);
    return mode;
  }

  // Finalize: compute bbox, select all entities in it, return to idle.
  finalize(): void {
    const bbox = this.getBbox();
    this.editor.overlays.clearStatusMessage("box-select");

    // Delegate to the content to select entities within the bbox.
    this.editor.content.selectAllInBbox(bbox, this.additive);
    this.editor.goUiIdle();
  }

  override handleMouseUp = () => {
    this.finalize();
  };
}
